import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMundusAnalytics } from '../lib/analytics.api';
import type { MundusAnalytics as MundusAnalyticsType } from '../lib/analytics.api';
import SpiderChart from '../components/charts/SpiderChart';
import TimeSeriesChart from '../components/charts/TimeSeriesChart';
import ProgressChart from '../components/charts/ProgressChart';

const TIME_RANGES = [
  { value: '7d', label: '7 Days' },
  { value: '30d', label: '30 Days' },
  { value: '90d', label: '90 Days' },
  { value: '1y', label: '1 Year' },
];

export default function MundusAnalytics() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState<MundusAnalyticsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedMetric, setSelectedMetric] = useState<number>(0);

  useEffect(() => {
    if (id) {
      loadAnalytics();
    }
  }, [id, timeRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getMundusAnalytics(id!, timeRange);
      setAnalytics(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm text-slate-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Analytics not found'}</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const selectedMetricData = analytics.metricEvolution[selectedMetric];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate(`/mundus/${id}`)}
                className="text-slate-600 hover:text-blue-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">
                  {analytics.mundus.name} Analytics
                </h1>
                <p className="text-xs text-slate-600 mt-1">
                  {analytics.mundus.description || 'Temporal evolution & performance metrics'}
                </p>
              </div>
            </div>

            {/* Time Range Selector */}
            <div className="flex items-center space-x-2">
              <span className="text-xs text-slate-500 mr-2">TIME RANGE:</span>
              {TIME_RANGES.map((range) => (
                <button
                  key={range.value}
                  onClick={() => setTimeRange(range.value)}
                  className={`px-3 py-1 rounded text-xs transition-all ${
                    timeRange === range.value
                      ? 'bg-blue-600 text-white border border-blue-600'
                      : 'bg-white text-slate-600 border border-slate-300 hover:border-blue-600'
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
            <div className="text-xs text-slate-500 mb-2">METRICS</div>
            <div className="text-3xl font-bold text-blue-600">
              {analytics.overview.metricsCount}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
            <div className="text-xs text-slate-500 mb-2">AGENTS</div>
            <div className="text-3xl font-bold text-purple-600">
              {analytics.overview.agentsCount}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
            <div className="text-xs text-slate-500 mb-2">REPORTS</div>
            <div className="text-3xl font-bold text-green-600">
              {analytics.overview.reportsCount}
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
            <div className="text-xs text-slate-500 mb-2">LATEST SCORE</div>
            <div className="text-3xl font-bold text-pink-600">
              {analytics.overview.latestScore}
            </div>
          </div>
        </div>

        {/* Spider Chart & Report Scores */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
            <SpiderChart
              data={analytics.spiderData}
              title="Metrics Overview (Spider Chart)"
              color="#2563eb"
            />
          </div>
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
            <TimeSeriesChart
              data={analytics.reportScores}
              title="Report Scores Over Time"
              yAxisLabel="Score"
              color="#9333ea"
              showArea={true}
            />
          </div>
        </div>

        {/* Metric Progress */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 mb-8">
          <ProgressChart
            data={analytics.metricEvolution.map(m => ({
              name: m.metricName,
              progress: m.progress,
              current: m.currentValue,
              target: m.targetValue,
            }))}
            title="Metrics Progress"
          />
        </div>

        {/* Metric Evolution */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 mb-8">
          <h2 className="text-xl font-bold text-slate-900 mb-4">
            Metric Evolution Details
          </h2>

          {/* Metric Selector */}
          <div className="flex flex-wrap gap-2 mb-6">
            {analytics.metricEvolution.map((metric, index) => (
              <button
                key={metric.metricId}
                onClick={() => setSelectedMetric(index)}
                className={`px-4 py-2 rounded-lg text-sm transition-all ${
                  selectedMetric === index
                    ? 'bg-blue-600 text-white border border-blue-600'
                    : 'bg-slate-50 text-slate-700 border border-slate-300 hover:border-blue-600'
                }`}
              >
                {metric.metricName}
              </button>
            ))}
          </div>

          {selectedMetricData && (
            <div>
              {/* Metric Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <div className="text-xs text-slate-500 mb-1">CURRENT</div>
                  <div className="text-xl font-bold text-blue-600">
                    {selectedMetricData.currentValue} {selectedMetricData.unit}
                  </div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <div className="text-xs text-slate-500 mb-1">TARGET</div>
                  <div className="text-xl font-bold text-green-600">
                    {selectedMetricData.targetValue} {selectedMetricData.unit}
                  </div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <div className="text-xs text-slate-500 mb-1">PROGRESS</div>
                  <div className="text-xl font-bold text-purple-600">
                    {selectedMetricData.progress.toFixed(1)}%
                  </div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <div className="text-xs text-slate-500 mb-1">DATA POINTS</div>
                  <div className="text-xl font-bold text-slate-900">
                    {selectedMetricData.evolution.length}
                  </div>
                </div>
              </div>

              {/* Metric Evolution Chart */}
              <TimeSeriesChart
                data={selectedMetricData.evolution}
                title={`${selectedMetricData.metricName} Evolution`}
                yAxisLabel={selectedMetricData.unit}
                color="#2563eb"
              />
            </div>
          )}
        </div>

        {/* Agent Activity */}
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-6">
          <h2 className="text-xl font-bold text-slate-900 mb-4">
            Agent Activity
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {analytics.agentActivity.map((agent) => (
              <div
                key={agent.id}
                className="bg-slate-50 border border-slate-200 rounded-lg p-4"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="font-bold text-slate-900">
                    {agent.name}
                  </h3>
                  <span
                    className={`px-2 py-1 rounded text-xs ${
                      agent.isActive
                        ? 'bg-green-50 text-green-700 border border-green-200'
                        : 'bg-slate-100 text-slate-500 border border-slate-300'
                    }`}
                  >
                    {agent.isActive ? 'ACTIVE' : 'IDLE'}
                  </span>
                </div>
                <div className="text-xs text-slate-500 mb-2">
                  TYPE: <span className="text-slate-700">{agent.type}</span>
                </div>
                <div className="text-xs text-slate-500 mb-2">
                  EXECUTIONS: <span className="text-blue-600 font-bold">{agent.executionCount}</span>
                </div>
                {agent.lastExecutedAt && (
                  <div className="text-xs text-slate-500">
                    LAST RUN:{' '}
                    <span className="text-slate-700">
                      {new Date(agent.lastExecutedAt).toLocaleString()}
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
